import {
  favoriteActions,
  favoriteStationActions,
  favoriteTrackActions,
  favoritePodcastActions,
  favoriteClipActions,
  gigyaActions,
} from 'actions/actions-const';
import CONTENT_TYPES from 'globalConst/contentTypes-const';
import { isEmpty } from 'util/index';

const initialState = {
  queue: {
    stations: [],
    tracks: [],
    podcasts: [],
    clips: [],
  },
  stations: [],
  tracks: [],
  podcasts: [],
  clips: [],
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case favoriteActions.FAVORITES_LOADED:
    case favoriteActions.ROLLBACK_FAVORITES:
      return {
        ...state,
        ...action.payload,
      };

    case favoriteStationActions.QUEUE_FAVORITE_STATION:
      return {
        ...state,
        queue: {
          ...state.queue,
          stations: [action.payload, ...state.queue.stations],
        },
      };
    case favoriteStationActions.FAVORITE_STATION:
      return {
        ...state,
        stations: [action.payload, ...state.stations],
      };
    case favoriteStationActions.UNFAVORITE_STATION:
      return {
        ...state,
        stations: state.stations.filter((station) => station.slug !== action.payload),
      };

    case favoriteTrackActions.QUEUE_FAVORITE_TRACK:
      return {
        ...state,
        queue: {
          ...state.queue,
          tracks: [action.payload, ...state.queue.tracks],
        },
      };
    case favoriteTrackActions.FAVORITE_TRACK:
      return {
        ...state,
        tracks: [action.payload, ...state.tracks],
      };
    case favoriteTrackActions.UNFAVORITE_TRACK:
      return {
        ...state,
        tracks: state.tracks.filter((track) => track.isrc !== action.payload),
      };

    case favoritePodcastActions.QUEUE_FAVORITE_PODCAST:
    case favoritePodcastActions.QUEUE_FAVORITE_PODCAST_CLIP:
      return {
        ...state,
        queue: {
          ...state.queue,
          podcasts: [action.payload, ...state.queue.podcasts],
        },
      };
    case favoritePodcastActions.FAVORITE_PODCAST:
    case favoritePodcastActions.FAVORITE_PODCAST_CLIP:
      return {
        ...state,
        podcasts: [action.payload, ...state.podcasts],
      };
    case favoritePodcastActions.UNFAVORITE_PODCAST:
    case favoritePodcastActions.UNFAVORITE_PODCAST_CLIP:
      return {
        ...state,
        podcasts: state.podcasts.filter((podcast) => podcast.id !== action.payload),
      };

    case favoriteClipActions.QUEUE_FAVORITE_CLIP:
      return {
        ...state,
        queue: {
          ...state.queue,
          clips: [action.payload, ...state.queue.clips],
        },
      };
    case favoriteClipActions.FAVORITE_CLIP:
      return {
        ...state,
        clips: [action.payload, ...state.clips],
      };
    case favoriteClipActions.UNFAVORITE_CLIP:
      return {
        ...state,
        clips: state.clips.filter((clip) => clip.id !== action.payload),
      };

    case favoriteActions.CLEAR_FAVORITE_QUEUE:
      return {
        ...state,
        queue: {
          stations: [],
          tracks: [],
          podcasts: [],
          clips: [],
        },
      };

    case gigyaActions.LOGOUT:
      return initialState;
    default:
      return state;
  }
}

/**
 * Given the current state, it returns the favorite stations
 *
 * @param {object} state
 * @returns {array}
 */
export const getFavoriteStations = (state) => state.favorites.stations;

/**
 * Given the current state, it returns the favorite tracks
 *
 * @param {object} state
 * @returns {array}
 */
export const getFavoriteTracks = (state) => state.favorites.tracks;

/**
 * Given the current state, it returns the favorite podcasts
 *
 * @param {object} state
 * @returns {array}
 */
export const getFavoritePodcasts = (state) => state.favorites.podcasts;

/**
 * Given the current state, it returns the favorite clips
 *
 * @param {object} state
 * @returns {array}
 */
export const getFavoriteClips = (state) => state.favorites.clips;

/**
 * Given the current state and props, it returns whether or not a station is favorite
 *
 * @param {object} state
 * @param {object} props
 * @returns {boolean}
 */
export const getIsFavoriteStation = (state, props) =>
  state.favorites.stations.some(
    (favoriteStation) => favoriteStation.slug === props.slug || favoriteStation.stationSlug === props.slug
  );

/**
 * Given the current state and props, it returns whether or not a track is favorite
 *
 * @param {object} state
 * @param {object} props
 * @returns {boolean}
 */
export const getIsFavoriteTrack = (state, props) =>
  props.track &&
  props.track.isrc &&
  state.favorites &&
  state.favorites.tracks &&
  state.favorites.tracks.some((favoriteTrack) => favoriteTrack.isrc === props.track.isrc);

/**
 * Given the current state and props, it returns whether or not a podcast is favorite
 *
 * @param {object} state
 * @param {object} podcast
 * @returns {boolean}
 */
export const getIsFavoritePodcast = (state, podcast) => {
  if (isEmpty(podcast)) {
    return false;
  }
  const { id, slug } = podcast;
  if (slug) {
    return getFavoritePodcasts(state).some(
      (favoritePodcast) => favoritePodcast.slug === slug || favoritePodcast.podcastSlug === slug
    );
  }

  if (id) {
    return getFavoritePodcasts(state).some((favoritePodcast) => favoritePodcast.id === id);
  }

  return false;
};

/**
 * Given the current state and props, it returns whether or not a clip is favorite
 *
 * @param {object} state
 * @param {object} clip
 * @returns {boolean}
 */
export const getIsFavoriteClip = (state, props) =>
  props && props.id && state.favorites.clips.some((favoriteClip) => favoriteClip.id === props.id);

/**
 * Given the current state, and props it returns whether or not the item is favorite
 * it requires a props.type and props.item
 *
 * @param {object} state
 * @param {object} props
 * @returns {boolean}
 */
export const getIsFavorite = (state, { item }) => {
  if (item.contentTypeId === CONTENT_TYPES.PODCAST) return getIsFavoritePodcast(state, item);
  if (item.contentTypeId === CONTENT_TYPES.STATION) return getIsFavoriteStation(state, item);
  if (item.contentTypeId === CONTENT_TYPES.TRACK) return getIsFavoriteTrack(state, item);
  if (item.contentTypeId === CONTENT_TYPES.CLIP) return getIsFavoriteClip(state, item);

  return false;
};
